import { useCallback, useEffect, useState } from "react";
import { create } from "zustand";
import { createJSONStorage, persist, StateStorage } from "zustand/middleware";

export type ChatNavSectionKey = "unread" | "group" | "direct";

const sanitizeCollapsed = (value: unknown): Record<string, boolean> => {
  if (!value || typeof value !== "object") return {};
  const collapsed: Record<string, boolean> = {};
  for (const [section, isCollapsed] of Object.entries(value)) {
    if (typeof isCollapsed === "boolean") collapsed[section] = isCollapsed;
  }
  return collapsed;
};

type ChatNavCollapsedState = {
  collapsed: Record<string, boolean>;
  setCollapsed: (section: ChatNavSectionKey, collapsed: boolean) => void;
};

const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

const safeLocalStorage: StateStorage = {
  getItem: (name) => {
    try {
      return window.localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      window.localStorage.setItem(name, value);
    } catch {
      return;
    }
  },
  removeItem: (name) => {
    try {
      window.localStorage.removeItem(name);
    } catch {
      return;
    }
  },
};

export const useChatNavCollapsedStore = create<ChatNavCollapsedState>()(
  persist(
    (set) => ({
      collapsed: {},
      setCollapsed: (section, collapsed) =>
        set((state) => ({
          collapsed: { ...state.collapsed, [section]: collapsed },
        })),
    }),
    {
      name: "messaging-nav-collapsed-sections",
      version: 1,
      storage: createJSONStorage(() =>
        typeof window === "undefined" ? noopStorage : safeLocalStorage,
      ),
      partialize: (state) => ({ collapsed: state.collapsed }),
      merge: (persisted, current) => ({
        ...current,
        collapsed: sanitizeCollapsed(
          (persisted as { collapsed?: unknown } | null)?.collapsed,
        ),
      }),
    },
  ),
);

// Persisted collapsed state hydrates from localStorage asynchronously, so
// consumers that must not flash open-then-collapsed should wait on this.
export function useChatNavCollapsedHasHydrated(): boolean {
  const [hasHydrated, setHasHydrated] = useState(() =>
    useChatNavCollapsedStore.persist.hasHydrated(),
  );
  useEffect(() => {
    setHasHydrated(useChatNavCollapsedStore.persist.hasHydrated());
    return useChatNavCollapsedStore.persist.onFinishHydration(() =>
      setHasHydrated(true),
    );
  }, []);
  return hasHydrated;
}

export function useChatNavCollapsed(section: ChatNavSectionKey): {
  collapsed: boolean;
  setCollapsed: (next: boolean) => void;
} {
  const collapsed = useChatNavCollapsedStore(
    (state) => state.collapsed[section] ?? false,
  );
  const setCollapsed = useCallback(
    (next: boolean) =>
      useChatNavCollapsedStore.getState().setCollapsed(section, next),
    [section],
  );
  return { collapsed, setCollapsed };
}
